import { useState } from "react";
import { Button } from "./button";
import { Input } from "./input";
import { Badge } from "./badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faTimes,
  faCalendarAlt,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";

const DateFilter = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApply,
  onClear,
  isFilterApplied,
  totalItems = 0,
  label = "records",
  className = "",
}) => {
  const [dateError, setDateError] = useState("");

  const formatDateShort = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDateRangeText = () => {
    if (isFilterApplied && startDate && endDate) {
      return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`;
    }
    return "All Records";
  };

  const handleStartDateChange = (value) => {
    onStartDateChange(value);
    setDateError("");
    if (endDate && value && new Date(value) > new Date(endDate)) {
      setDateError("Start date cannot be after end date.");
    }
  };

  const handleEndDateChange = (value) => {
    onEndDateChange(value);
    setDateError("");
    if (startDate && value && new Date(value) < new Date(startDate)) {
      setDateError("End date must be greater than or equal to start date.");
    }
  };

  const handleApply = () => {
    if (!startDate || !endDate) {
      setDateError("Please select both start date and end date.");
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) {
      setDateError("End date must be greater than or equal to start date.");
      return;
    }
    setDateError("");
    onApply();
  };

  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg p-4 ${className}`}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faFilter} className="text-blue-600 h-4 w-4" />
          <span className="text-sm font-medium text-gray-700">
            Filter by date:
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 font-medium">From:</span>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="h-9 text-sm border border-gray-300 rounded-md w-36 sm:w-40"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 font-medium">To:</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              className="h-9 text-sm border border-gray-300 rounded-md w-36 sm:w-40"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleApply}
              disabled={!startDate || !endDate || !!dateError}
              className="h-9 bg-blue-800 hover:bg-blue-900 text-white px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </Button>
            {isFilterApplied && (
              <Button
                onClick={onClear}
                variant="outline"
                className="h-9 border-red-300 text-red-600 hover:bg-red-50 px-4 text-sm"
              >
                <FontAwesomeIcon icon={faTimes} className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {dateError && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2 flex items-center gap-2">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="h-3.5 w-3.5 text-red-500"
          />
          <span>{dateError}</span>
        </div>
      )}

      {!dateError && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <span className="text-gray-600">Showing:</span>
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-200"
          >
            <FontAwesomeIcon icon={faCalendarAlt} className="h-3 w-3 mr-1" />
            {getDateRangeText()}
          </Badge>
          <span className="text-gray-500">|</span>
          <span className="text-gray-600">
            <strong>{totalItems}</strong> {label} found
          </span>
        </div>
      )}
    </div>
  );
};

export default DateFilter;
